import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import VinDisplay from '../shared/VinDisplay';

// Format currency
const formatCurrency = (val) => {
  if (!val && val !== 0) return '-';
  return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Format days
const formatDays = (val) => {
  if (!val && val !== 0) return '-';
  return `${Number(val).toFixed(1)} days`;
};

// Vehicle Timeline Modal
const VehicleTimelineModal = ({ vehicleId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await api.getVehicleTimeline(vehicleId);
        setData(result);
      } catch (err) {
        console.error('Failed to load timeline:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [vehicleId]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">Loading timeline...</div>
      </div>
    );
  }

  if (!data) return null;

  const { vehicle, milestones, stageDurations, repairOrders } = data;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">{vehicle.year} {vehicle.make} {vehicle.model}</h2>
            <p className="text-sm text-neutral-500 flex items-center gap-2">Stock: {vehicle.stockNumber} | VIN: <VinDisplay vin={vehicle.vin} /></p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl">&times;</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Stage Durations Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stageDurations.purchaseToDelivery !== undefined && (
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-700">{stageDurations.purchaseToDelivery}</div>
                <div className="text-xs text-blue-600">Days to Delivery</div>
              </div>
            )}
            {stageDurations.deliveryToInspection !== undefined && (
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-700">{stageDurations.deliveryToInspection}</div>
                <div className="text-xs text-yellow-600">Days to Inspection</div>
              </div>
            )}
            {stageDurations.inspectionToFrontline !== undefined && (
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-700">{stageDurations.inspectionToFrontline}</div>
                <div className="text-xs text-green-600">Days in Recon</div>
              </div>
            )}
            {stageDurations.totalToFrontline !== undefined && (
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-purple-700">{stageDurations.totalToFrontline}</div>
                <div className="text-xs text-purple-600">Total to Frontline</div>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div>
            <h3 className="font-semibold mb-3">Timeline</h3>
            <div className="relative">
              {milestones.map((m, idx) => (
                <div key={idx} className="flex items-start gap-4 pb-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full ${
                      m.type === 'purchase' ? 'bg-blue-500' :
                      m.type === 'transport' ? 'bg-yellow-500' :
                      m.type === 'inspection' ? 'bg-orange-500' :
                      m.type === 'ro' ? 'bg-purple-500' :
                      m.type === 'frontline' ? 'bg-green-500' :
                      m.type === 'sold' ? 'bg-emerald-600' : 'bg-neutral-400'
                    }`} />
                    {idx < milestones.length - 1 && <div className="w-0.5 h-8 bg-neutral-200" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{m.event}</div>
                    <div className="text-xs text-neutral-500">{new Date(m.date).toLocaleString()}</div>
                  </div>
                </div>
              ))}
              {milestones.length === 0 && (
                <div className="text-neutral-400 text-sm">No timeline events recorded</div>
              )}
            </div>
          </div>

          {/* Repair Orders */}
          {repairOrders.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Repair Orders</h3>
              <div className="space-y-2">
                {repairOrders.map(ro => (
                  <div key={ro.id} className="bg-neutral-50 rounded p-3 flex justify-between items-center">
                    <div>
                      <span className="font-medium">{ro.roNumber}</span>
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                        ro.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>{ro.status}</span>
                    </div>
                    <div className="text-sm text-neutral-600">
                      {formatCurrency(ro.totalCost)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Financial Summary */}
          <div>
            <h3 className="font-semibold mb-3">Financial Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-neutral-500">Purchase Price</div>
                <div className="font-medium">{formatCurrency(vehicle.purchasePrice)}</div>
              </div>
              <div>
                <div className="text-neutral-500">Recon Cost</div>
                <div className="font-medium">{formatCurrency(vehicle.reconCost)}</div>
              </div>
              <div>
                <div className="text-neutral-500">Transport Cost</div>
                <div className="font-medium">{formatCurrency(vehicle.transportCost)}</div>
              </div>
              <div>
                <div className="text-neutral-500">Sale Price</div>
                <div className="font-medium">{formatCurrency(vehicle.dealSalePrice || vehicle.salePrice)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Analytics Dashboard
const AnalyticsDashboard = () => {
  const [view, setView] = useState('overview'); // overview, bottlenecks, auctions, vehicles, weekly
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [bottlenecks, setBottlenecks] = useState(null);
  const [auctions, setAuctions] = useState([]);
  const [makeModels, setMakeModels] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [metricsData, bottlenecksData] = await Promise.all([
        api.getAnalyticsMetrics(dateRange.start, dateRange.end),
        api.getAnalyticsBottlenecks()
      ]);
      setMetrics(metricsData);
      setBottlenecks(bottlenecksData);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadAuctions = async () => {
    try {
      const data = await api.getAuctionPerformance(dateRange.start, dateRange.end);
      setAuctions(data);
    } catch (err) {
      console.error('Failed to load auctions:', err);
    }
  };

  const loadMakeModels = async () => {
    try {
      const data = await api.getMakeModelAnalysis(dateRange.start, dateRange.end);
      setMakeModels(data);
    } catch (err) {
      console.error('Failed to load make/models:', err);
    }
  };

  const loadVendors = async () => {
    try {
      const data = await api.getVendorPerformance(dateRange.start, dateRange.end);
      setVendors(data);
    } catch (err) {
      console.error('Failed to load vendors:', err);
    }
  };

  const loadVehicles = async () => {
    try {
      const data = await api.getAnalyticsVehicles(dateRange.start, dateRange.end);
      setVehicles(data);
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    }
  };

  const loadWeeklyReport = async () => {
    try {
      const data = await api.getWeeklyReport();
      setWeeklyReport(data);
    } catch (err) {
      console.error('Failed to load weekly report:', err);
    }
  };

  useEffect(() => {
    if (view === 'auctions') loadAuctions();
    if (view === 'makemodel') loadMakeModels();
    if (view === 'vendors') loadVendors();
    if (view === 'vehicles') loadVehicles();
    if (view === 'weekly') loadWeeklyReport();
  }, [view, dateRange]);

  // Export to CSV
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading && !metrics) {
    return <div className="p-6 text-neutral-500">Loading analytics...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Analytics & Reports</h1>
        <div className="flex gap-2 items-center">
          <input
            type="date"
            value={dateRange.start}
            onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            className="px-2 py-1 border rounded text-sm"
          />
          <span className="text-neutral-400">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            className="px-2 py-1 border rounded text-sm"
          />
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'bottlenecks', label: 'Bottlenecks' },
          { id: 'auctions', label: 'Auctions' },
          { id: 'makemodel', label: 'Make/Model' },
          { id: 'vendors', label: 'Vendors' },
          { id: 'vehicles', label: 'Vehicles' },
          { id: 'weekly', label: 'Weekly Report' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setView(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
              view === tab.id ? 'bg-blue-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {view === 'overview' && metrics && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <div className="text-3xl font-bold text-blue-600">{metrics.frontline.avgDaysToFrontline.toFixed(1)}</div>
              <div className="text-sm text-neutral-500">Avg Days to Frontline</div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="text-3xl font-bold text-green-600">{formatCurrency(metrics.profit.avgGrossProfit)}</div>
              <div className="text-sm text-neutral-500">Avg Gross Profit</div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="text-3xl font-bold text-purple-600">{formatCurrency(metrics.recon.avgReconCost)}</div>
              <div className="text-sm text-neutral-500">Avg Recon Cost</div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="text-3xl font-bold text-orange-600">{metrics.repairOrders.avgDays.toFixed(1)}</div>
              <div className="text-sm text-neutral-500">Avg RO Days</div>
            </div>
          </div>

          {/* Vehicle Status */}
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Vehicle Status</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-yellow-600">{metrics.vehicles.inProgress}</div>
                <div className="text-sm text-neutral-500">In Progress</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{metrics.vehicles.frontlineReady}</div>
                <div className="text-sm text-neutral-500">Frontline Ready</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{metrics.vehicles.sold}</div>
                <div className="text-sm text-neutral-500">Sold</div>
              </div>
            </div>
          </div>

          {/* Profit Summary */}
          <div className="bg-white rounded-lg border p-4">
            <h3 className="font-semibold mb-4">Profit Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-neutral-500 text-sm">Deals</div>
                <div className="text-xl font-bold">{metrics.profit.dealsCount}</div>
              </div>
              <div>
                <div className="text-neutral-500 text-sm">Total Sales</div>
                <div className="text-xl font-bold">{formatCurrency(metrics.profit.totalSales)}</div>
              </div>
              <div>
                <div className="text-neutral-500 text-sm">Total Recon</div>
                <div className="text-xl font-bold text-red-600">{formatCurrency(metrics.recon.totalReconCost)}</div>
              </div>
              <div>
                <div className="text-neutral-500 text-sm">Total Gross Profit</div>
                <div className={`text-xl font-bold ${metrics.profit.totalGrossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(metrics.profit.totalGrossProfit)}
                </div>
              </div>
            </div>
          </div>

          {/* Current Bottlenecks Alert */}
          {bottlenecks && (bottlenecks.roAwaitingApproval.length > 0 || bottlenecks.partsDelayed.length > 0 || bottlenecks.reconOverdue.length > 0) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-2">Current Bottlenecks</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-red-600">{bottlenecks.roAwaitingApproval.length}</div>
                  <div className="text-sm text-red-700">ROs Awaiting Approval (24h+)</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-600">{bottlenecks.partsDelayed.length}</div>
                  <div className="text-sm text-orange-700">Parts Delayed (3+ days)</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{bottlenecks.reconOverdue.length}</div>
                  <div className="text-sm text-yellow-700">Vehicles Overdue (10+ days)</div>
                </div>
              </div>
              <button
                onClick={() => setView('bottlenecks')}
                className="mt-3 text-sm text-red-600 hover:underline"
              >
                View Details →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Bottlenecks View */}
      {view === 'bottlenecks' && bottlenecks && (
        <div className="space-y-6">
          {/* ROs Awaiting Approval */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-4 py-3 bg-red-50 border-b border-red-200">
              <h3 className="font-semibold text-red-800">🚨 ROs Awaiting Approval ({bottlenecks.roAwaitingApproval.length})</h3>
              <p className="text-sm text-red-600">Waiting more than 24 hours for approval</p>
            </div>
            {bottlenecks.roAwaitingApproval.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b bg-neutral-50">
                    <th className="px-4 py-2">RO #</th>
                    <th>Vehicle</th>
                    <th>Estimate</th>
                    <th>Hours Waiting</th>
                  </tr>
                </thead>
                <tbody>
                  {bottlenecks.roAwaitingApproval.map(ro => (
                    <tr key={ro.id} className="border-b hover:bg-red-50">
                      <td className="px-4 py-2 font-mono">{ro.roNumber}</td>
                      <td className="py-2">{ro.stockNumber} - {ro.year} {ro.make} {ro.model}</td>
                      <td className="py-2">{formatCurrency(ro.estimateTotal)}</td>
                      <td className="py-2 text-red-600 font-medium">{Math.round(ro.hoursWaiting)}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-4 text-neutral-400 text-center">No ROs waiting for approval</div>
            )}
          </div>

          {/* Parts Delayed */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-4 py-3 bg-orange-50 border-b border-orange-200">
              <h3 className="font-semibold text-orange-800">Parts Delayed ({bottlenecks.partsDelayed.length})</h3>
              <p className="text-sm text-orange-600">On order for more than 3 days</p>
            </div>
            {bottlenecks.partsDelayed.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b bg-neutral-50">
                    <th className="px-4 py-2">Part</th>
                    <th>RO #</th>
                    <th>Vehicle</th>
                    <th>Days Waiting</th>
                    <th>ETA</th>
                  </tr>
                </thead>
                <tbody>
                  {bottlenecks.partsDelayed.map(part => (
                    <tr key={part.id} className="border-b hover:bg-orange-50">
                      <td className="px-4 py-2">{part.partName}</td>
                      <td className="py-2 font-mono">{part.roNumber}</td>
                      <td className="py-2">{part.stockNumber} - {part.year} {part.make} {part.model}</td>
                      <td className="py-2 text-orange-600 font-medium">{Math.round(part.daysWaiting)} days</td>
                      <td className="py-2">{part.eta ? new Date(part.eta).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-4 text-neutral-400 text-center">No delayed parts</div>
            )}
          </div>

          {/* Vehicles Overdue */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-200">
              <h3 className="font-semibold text-yellow-800">Vehicles Overdue ({bottlenecks.reconOverdue.length})</h3>
              <p className="text-sm text-yellow-600">In recon for more than 10 days</p>
            </div>
            {bottlenecks.reconOverdue.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b bg-neutral-50">
                    <th className="px-4 py-2">Stock</th>
                    <th>Vehicle</th>
                    <th>Status</th>
                    <th>Days in Recon</th>
                    <th>Open ROs</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {bottlenecks.reconOverdue.map(v => (
                    <tr key={v.id} className="border-b hover:bg-yellow-50">
                      <td className="px-4 py-2 font-mono">{v.stockNumber}</td>
                      <td className="py-2">{v.year} {v.make} {v.model}</td>
                      <td className="py-2">{v.reconStatus}</td>
                      <td className="py-2 text-yellow-600 font-medium">{Math.round(v.daysInRecon)} days</td>
                      <td className="py-2">{v.openRos}</td>
                      <td className="py-2">
                        <button
                          onClick={() => setSelectedVehicleId(v.id)}
                          className="text-blue-600 hover:underline text-xs"
                        >
                          View Timeline
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-4 text-neutral-400 text-center">No overdue vehicles</div>
            )}
          </div>
        </div>
      )}

      {/* Auctions View */}
      {view === 'auctions' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b flex justify-between items-center">
            <h3 className="font-semibold">Auction Performance</h3>
            <button
              onClick={() => exportToCSV(auctions, 'auction_performance')}
              className="text-sm text-blue-600 hover:underline"
            >
              Export CSV
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b bg-neutral-50">
                <th className="px-4 py-2">Auction</th>
                <th className="text-center">Vehicles</th>
                <th className="text-center">Sold</th>
                <th className="text-right">Avg Recon</th>
                <th className="text-right">Avg Days</th>
                <th className="text-right">Avg Profit</th>
                <th className="text-right">Total Profit</th>
                <th className="text-center">High Recon</th>
              </tr>
            </thead>
            <tbody>
              {auctions.map((a, idx) => (
                <tr key={idx} className="border-b hover:bg-neutral-50">
                  <td className="px-4 py-2 font-medium">{a.auctionName}</td>
                  <td className="py-2 text-center">{a.totalVehicles}</td>
                  <td className="py-2 text-center">{a.soldCount}</td>
                  <td className="py-2 text-right">{formatCurrency(a.avgReconCost)}</td>
                  <td className="py-2 text-right">{a.avgDaysToFrontline ? a.avgDaysToFrontline.toFixed(1) : '-'}</td>
                  <td className={`py-2 text-right font-medium ${a.avgProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(a.avgProfit)}
                  </td>
                  <td className={`py-2 text-right font-medium ${a.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(a.totalProfit)}
                  </td>
                  <td className="py-2 text-center">
                    {a.highReconCount > 0 && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">{a.highReconCount}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {auctions.length === 0 && (
            <div className="p-4 text-neutral-400 text-center">No auction data for this period</div>
          )}
        </div>
      )}

      {/* Make/Model View */}
      {view === 'makemodel' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b flex justify-between items-center">
            <h3 className="font-semibold">Make/Model Performance</h3>
            <button
              onClick={() => exportToCSV(makeModels, 'make_model_performance')}
              className="text-sm text-blue-600 hover:underline"
            >
              Export CSV
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b bg-neutral-50">
                <th className="px-4 py-2">Make</th>
                <th>Model</th>
                <th className="text-center">Count</th>
                <th className="text-right">Avg Recon</th>
                <th className="text-right">Avg Days</th>
                <th className="text-right">Avg Profit</th>
                <th className="text-center">High Recon</th>
              </tr>
            </thead>
            <tbody>
              {makeModels.map((m, idx) => (
                <tr key={idx} className="border-b hover:bg-neutral-50">
                  <td className="px-4 py-2 font-medium">{m.make}</td>
                  <td className="py-2">{m.model}</td>
                  <td className="py-2 text-center">{m.totalVehicles}</td>
                  <td className="py-2 text-right">{formatCurrency(m.avgReconCost)}</td>
                  <td className="py-2 text-right">{m.avgDaysToFrontline ? m.avgDaysToFrontline.toFixed(1) : '-'}</td>
                  <td className={`py-2 text-right font-medium ${m.avgProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(m.avgProfit)}
                  </td>
                  <td className="py-2 text-center">
                    {m.highReconCount > 0 && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">{m.highReconCount}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {makeModels.length === 0 && (
            <div className="p-4 text-neutral-400 text-center">No make/model data for this period</div>
          )}
        </div>
      )}

      {/* Vendors View */}
      {view === 'vendors' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b flex justify-between items-center">
            <h3 className="font-semibold">Vendor Performance</h3>
            <button
              onClick={() => exportToCSV(vendors, 'vendor_performance')}
              className="text-sm text-blue-600 hover:underline"
            >
              Export CSV
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b bg-neutral-50">
                <th className="px-4 py-2">Vendor</th>
                <th>Type</th>
                <th className="text-center">Jobs</th>
                <th className="text-right">Total Cost</th>
                <th className="text-right">Avg Cost</th>
                <th className="text-right">Avg Turnaround</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v, idx) => (
                <tr key={idx} className="border-b hover:bg-neutral-50">
                  <td className="px-4 py-2 font-medium">{v.vendorName}</td>
                  <td className="py-2">{v.vendorType}</td>
                  <td className="py-2 text-center">{v.jobsCount}</td>
                  <td className="py-2 text-right">{formatCurrency(v.totalCost)}</td>
                  <td className="py-2 text-right">{formatCurrency(v.avgCost)}</td>
                  <td className="py-2 text-right">{v.avgTurnaroundDays ? `${v.avgTurnaroundDays.toFixed(1)} days` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {vendors.length === 0 && (
            <div className="p-4 text-neutral-400 text-center">No vendor data for this period</div>
          )}
        </div>
      )}

      {/* Vehicles View */}
      {view === 'vehicles' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b flex justify-between items-center">
            <h3 className="font-semibold">Vehicle Timeline Analysis</h3>
            <button
              onClick={() => exportToCSV(vehicles, 'vehicle_analysis')}
              className="text-sm text-blue-600 hover:underline"
            >
              Export CSV
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b bg-neutral-50">
                <th className="px-4 py-2">Stock</th>
                <th>Vehicle</th>
                <th>Auction</th>
                <th className="text-right">Purchase</th>
                <th className="text-right">Recon</th>
                <th className="text-right">Sale</th>
                <th className="text-center">Days</th>
                <th className="text-center">ROs</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map(v => (
                <tr key={v.id} className="border-b hover:bg-neutral-50">
                  <td className="px-4 py-2 font-mono text-xs">{v.stockNumber}</td>
                  <td className="py-2">{v.year} {v.make} {v.model}</td>
                  <td className="py-2 text-xs">{v.auctionName || '-'}</td>
                  <td className="py-2 text-right">{formatCurrency(v.purchasePrice)}</td>
                  <td className="py-2 text-right">{formatCurrency(v.reconCost)}</td>
                  <td className="py-2 text-right">{formatCurrency(v.salePrice)}</td>
                  <td className="py-2 text-center">
                    <span className={`${v.daysInRecon > 10 ? 'text-red-600 font-medium' : ''}`}>
                      {Math.round(v.daysInRecon)}
                    </span>
                  </td>
                  <td className="py-2 text-center">{v.roCount}</td>
                  <td className="py-2">
                    <button
                      onClick={() => setSelectedVehicleId(v.id)}
                      className="text-blue-600 hover:underline text-xs"
                    >
                      Timeline
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {vehicles.length === 0 && (
            <div className="p-4 text-neutral-400 text-center">No vehicles for this period</div>
          )}
        </div>
      )}

      {/* Weekly Report View */}
      {view === 'weekly' && weeklyReport && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold">Weekly Report</h2>
                <p className="text-neutral-500">
                  {new Date(weeklyReport.weekStart).toLocaleDateString()} - {new Date(weeklyReport.weekEnd).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => {
                  const reportData = [
                    { metric: 'Vehicles Acquired', value: weeklyReport.summary.vehiclesAcquired },
                    { metric: 'Vehicles Delivered', value: weeklyReport.summary.vehiclesDelivered },
                    { metric: 'ROs Completed', value: weeklyReport.summary.rosCompleted },
                    { metric: 'Total Recon Cost', value: weeklyReport.summary.totalReconCost },
                    { metric: 'Vehicles Sold', value: weeklyReport.summary.vehiclesSold },
                    { metric: 'Total Sales', value: weeklyReport.summary.totalSales },
                    { metric: 'Total Profit', value: weeklyReport.summary.totalProfit },
                    { metric: 'Avg Days to Frontline', value: weeklyReport.summary.avgDaysToFrontline }
                  ];
                  exportToCSV(reportData, 'weekly_report');
                }}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                Export Report
              </button>
            </div>

            {/* Summary Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-blue-700">{weeklyReport.summary.vehiclesAcquired}</div>
                <div className="text-sm text-blue-600">Acquired</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-yellow-700">{weeklyReport.summary.vehiclesDelivered}</div>
                <div className="text-sm text-yellow-600">Delivered</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-green-700">{weeklyReport.summary.rosCompleted}</div>
                <div className="text-sm text-green-600">ROs Completed</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-purple-700">{weeklyReport.summary.vehiclesSold}</div>
                <div className="text-sm text-purple-600">Sold</div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <div className="text-neutral-500 text-sm">Total Sales</div>
                <div className="text-xl font-bold">{formatCurrency(weeklyReport.summary.totalSales)}</div>
              </div>
              <div>
                <div className="text-neutral-500 text-sm">Total Recon Cost</div>
                <div className="text-xl font-bold text-red-600">{formatCurrency(weeklyReport.summary.totalReconCost)}</div>
              </div>
              <div>
                <div className="text-neutral-500 text-sm">Total Profit</div>
                <div className={`text-xl font-bold ${weeklyReport.summary.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(weeklyReport.summary.totalProfit)}
                </div>
              </div>
              <div>
                <div className="text-neutral-500 text-sm">Avg Days to Frontline</div>
                <div className="text-xl font-bold">{weeklyReport.summary.avgDaysToFrontline?.toFixed(1) || '-'}</div>
              </div>
            </div>

            {/* Current Bottlenecks */}
            <div className="bg-red-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-red-800 mb-2">Current Bottlenecks</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-red-600">{weeklyReport.bottlenecks.rosAwaitingApproval}</div>
                  <div className="text-sm text-red-700">ROs Awaiting Approval</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-600">{weeklyReport.bottlenecks.partsDelayed}</div>
                  <div className="text-sm text-orange-700">Parts Delayed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{weeklyReport.bottlenecks.vehiclesOverdue}</div>
                  <div className="text-sm text-yellow-700">Vehicles Overdue</div>
                </div>
              </div>
            </div>

            {/* Auction Performance */}
            {weeklyReport.auctionPerformance?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Top Auctions This Week</h3>
                <div className="space-y-2">
                  {weeklyReport.auctionPerformance.map((a, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-neutral-50 rounded p-2">
                      <span className="font-medium">{a.name}</span>
                      <span className="text-sm text-neutral-600">{a.count} vehicles • Avg Recon: {formatCurrency(a.avgReconCost)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Slowest Vehicles */}
            {weeklyReport.slowestVehicles?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Slowest Vehicles This Week</h3>
                <div className="space-y-2">
                  {weeklyReport.slowestVehicles.map((v, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-yellow-50 rounded p-2">
                      <span>{v.stockNumber} - {v.year} {v.make} {v.model}</span>
                      <span className="text-sm">
                        <span className="text-yellow-700 font-medium">{v.daysToFrontline} days</span>
                        <span className="text-neutral-500 ml-2">Recon: {formatCurrency(v.reconCost)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vehicle Timeline Modal */}
      {selectedVehicleId && (
        <VehicleTimelineModal
          vehicleId={selectedVehicleId}
          onClose={() => setSelectedVehicleId(null)}
        />
      )}
    </div>
  );
};

export default AnalyticsDashboard;
